import { BaseLogger } from '../BaseLogger';
import { consoleLogProcessor } from '../LogProcessor/ConsoleLogProcessor';
import { FluidLogProcessor, HostLogProcessor } from '../LogProcessor';
/**
 * Implementation of the Logger interface that buffers events and delegates events to a LogProcessor once it is fully loaded.
 */
export class OfficeFluidLogger extends BaseLogger {
    /**
     * Constructor to initialize the Logger.
     */
    constructor(config) {
        const persistentProps = {
            ...config.persistentProps,
            hostCorrelationId: config.hostContextProps.hostCorrelationId,
            // HostScenarioName = <LifeCycle>_<Event/ScenarioName>_<EntryPoint>
            hostScenarioName: [
                config.hostContextProps.hostScenarioLifecycle,
                config.hostContextProps.hostScenarioName,
                config.hostContextProps.hostScenarioEntryPoint
            ]
                .filter((item) => item) // Filter only to non-empty values
                .join('_'),
            hostName: config.hostContextProps.hostName,
            hostScenarioStartTime: config.hostContextProps.hostScenarioStartTime,
            // Adding loopAudience here so it gets logged as Data_loopAudience
            // We found a bug where the Json Processor in Native Clients ignore the Loop_Audience field,
            // so we need to bring back the Data_loopAudience field
            loopAudience: config.hostContextProps.audience
        };
        super(persistentProps, config.namespace);
        this.forceTelemetryToConsole = false;
        /**
         * Prevents new events from being added for transmission. It also batches all
         * events currently in the queue, and creates requests for them to be sent.
         * Make sure that this is called after logging events related to session end.
         */
        this.shutdown = () => {
            this.logProcessor.shutdown();
        };
        const { hostContextProps, delayLoadLogger, hostTelemetrySink } = config;
        this.audience = hostContextProps.audience;
        try {
            this.forceTelemetryToConsole = localStorage.getItem('debugPurple') === '1';
        }
        catch {
            // Storage might not be available in some scenarios due to tracking protection.
            // Tracking protection settings are immutable within an app session so it is okay to detect
            // that state only once during the logger's lifetime.
        }
        // If hostTelemetrySink is provided, it means that we are going to use HostLogProcessor which doesn't require OfficeFluidOneDsSink loading
        if (hostTelemetrySink) {
            this.logProcessor = new HostLogProcessor(hostTelemetrySink, this.audience, persistentProps);
        }
        else {
            // The OtelLogProcessor has the logic to push events into a queue, so consumers of OtelJs APIs can delay load the one ds sink
            // Create an OtelLogProcessor right away, and pass the necessary information to the logProcessor
            this.logProcessor = new FluidLogProcessor({
                appPlatform: hostContextProps.hostAppPlatform,
                appName: hostContextProps.hostAppName,
                appVersion: hostContextProps.hostAppVersion,
                user: hostContextProps.userContext.user,
                sessionId: hostContextProps.hostSessionId,
                audience: hostContextProps.audience,
                uiLanguage: hostContextProps.hostLocaleContext?.uiLanguage
            }, persistentProps);
            // If delayLoadLogger is not passed or set as false, load the OneDSSink right away
            if (!delayLoadLogger) {
                this.loadFull().catch(() => {
                    // We do not need to explicitly handle the catch here as loadFull handles it internally
                });
            }
        }
        if (config.hostMetadata) {
            const { hostMetadata } = config;
            const hostFlights = hostMetadata.hostFlights;
            this.sendWithNamespace({
                eventName: 'HostMetadata',
                hostRing: hostMetadata.hostRing,
                hostFlights: hostFlights && JSON.stringify(hostFlights),
                category: 'Generic'
            }, 'HostMetadata' // namespace
            );
        }
    }
    get PersistedProperties() {
        return this;
    }
    /**
     * Calls the logProcessor's loadFull method, so the OneDsSink is loaded and events in the telemetryLogger's queue are sent to the pipeline.
     * The host application is responsible for calling this method if delayLoadLogger is set to true.
     * This is useful when hosts want to instantiate the logger but want to reduce the initial bits pulled down.
     * If delayLoadLogger is undefined or set to false, the OfficeFluidLogger calls the loadFull method from the constructor
     * Invoking this for a second or higher time will be a no-op.
     */
    async loadFull() {
        // Adding this back because we need to continue to expose loadFull as it was public method, but this can call loadFull from the logProcessor
        try {
            // if this is hostlogprocessor this is a no op
            await this.logProcessor.loadFull();
        }
        catch {
            // We do not need to explicitly handle the catch here as logProcessor.loadFull handles it internally
        }
    }
    /**
     * Sets special, well-known persistent fields for User Identity on the events logged through the logger.
     * These fields have to be logged in a certain way by the logProcessor.
     * For example, the OtelLogProcessor converts these fields such that it follows the Office Schema.
     */
    setUserContext(context) {
        this.logProcessor.setUserContext(context);
    }
    logEvent(event) {
        // In addition to sending the event to the registered logProcessor, log to console if forceTelemetryToConsole is set to true.
        // TODO: Investigate using the debug library to do the styling in the future.
        this.forceTelemetryToConsole && consoleLogProcessor.logEvent(event);
        this.logProcessor.logEvent(event);
    }
    addPersistedProperties(properties) {
        super.addPersistedProperties(properties);
        import(/* webpackChunkName: "AshaUtils" */ '../LogProcessor/AshaUtils')
            .then(({ updateAshaAdditionalPartCEventDataFromTelemetryProperties }) => updateAshaAdditionalPartCEventDataFromTelemetryProperties(properties))
            .catch(() => {
            this.send({
                category: 'Error',
                eventName: 'AshaUpdatePartCEventDataFailure'
            });
        });
    }
}
// cspell:ignore otel
//# sourceMappingURL=OfficeFluidLogger.js.map